<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="viewer-toolbar">
    <ul class="map-tools-list">
      <li><a :title="$t('zoom-in')" @click="$emit('run-command', 'zoomIn')"><i class="fas fa-search-plus fa-fw"></i></a></li>
      <li><a :title="$t('zoom-out')" @click="$emit('run-command', 'zoomOut')"><i class="fas fa-search-minus fa-fw"></i></a></li>
      <li v-if="isPanelDisplayed('digital-zoom')">
        <a @click="$emit('toggle-panel', 'digital-zoom')" :class="{ active: activePanel === 'digital-zoom' }">
          <i class="fas fa-search fa-fw"></i>
        </a>
        <digital-zoom class="panel-options" v-show="activePanel === 'digital-zoom'" :index="activeImageIndex"
          @resetZoom="$emit('run-command', 'resetZoom')" @fitZoom="$emit('run-command', 'fitZoom')" />
      </li>
      <li>
        <a :title="$t('rotate')" @click="$emit('toggle-panel', 'rotation')" :class="{ active: activePanel === 'rotation' }">
          <i class="fa fa-undo fa-fw" aria-hidden="true"></i>
        </a>
        <rotation-selector class="panel-options" v-show="activePanel === 'rotation'" :index="activeImageIndex" />
      </li>
      <hr class="is-divider">
      <li>
        <a :title="$t('ai-analysis')" :class="{ active: isOverlayActive('algorithm'), disabled: !canOpenImagePanel }"
          @click="toggleImageOverlay('algorithm')">
          <i class="fas fa-robot fa-fw"></i>
        </a>
      </li>
      <li>
        <a :title="$t('pathology-report')" :class="{ active: isOverlayActive('report'), disabled: !canOpenImagePanel }"
          @click="toggleImageOverlay('report')">
          <i class="fas fa-file-medical fa-fw"></i>
        </a>
      </li>
      <hr class="is-divider" />
      <li>
        <a @click="$emit('toggle-panel', 'layers')" :class="{ active: activePanel === 'layers' }">
          <i class="fas fa-copy fa-fw"></i>
        </a>
        <layers-panel class="panel-options" v-show="activePanel === 'layers'" :index="activeImageIndex" />
      </li>
      <li v-if="isPanelDisplayed('color-manipulation')">
        <a @click="$emit('toggle-panel', 'colors')" :class="{ active: activePanel === 'colors' }">
          <i class="fas fa-adjust fa-fw"></i>
        </a>
        <color-manipulation class="panel-options" v-show="activePanel === 'colors'" :index="activeImageIndex" />
      </li>
      <hr class="is-divider" />
      <li v-if="isPanelDisplayed('info')">
        <a @click="$emit('toggle-panel', 'info')" :class="{ active: ['info', 'metadata'].includes(activePanel) }">
          <i class="fas fa-info fa-fw"></i>
        </a>
        <information-panel class="panel-options" v-show="activePanel === 'info'" :index="activeImageIndex" />
      </li>
      <li v-if="configUi['project-tools-screenshot']">
        <a @click="$emit('run-command', 'takeScreenshot')" :class="{ active: activePanel === 'screenshot' }">
          <i class="fas fa-camera fa-fw"></i>
        </a>
      </li>
      <li>
        <a @click="$emit('toggle-fullscreen')">
          <i :class="isFullscreen ? 'fas fa-compress fa-fw' : 'fas fa-expand fa-fw'"></i>
        </a>
      </li>
      <li v-if="!$keycloak.hasTemporaryToken">
        <a @click="$emit('open-modal', 'share')">
          <i class="fa fa-share-alt fa-fw" aria-hidden="true"></i>
        </a>
      </li>
      <li>
        <a :title="$t('export-annotations')" @click="$emit('open-modal', 'export')">
          <i class="fas fa-file-export fa-fw"></i>
        </a>
      </li>
      <li v-if="isAuditLogPanelDisplayed">
        <a :title="$t('annotation-audit-logs')"
          :class="{ active: isOverlayActive('audit-log'), disabled: !canOpenImagePanel }"
          @click="toggleImageOverlay('audit-log')">
          <i class="fas fa-history fa-fw"></i>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'viewer-toolbar',
  components: {
    RotationSelector: () => import('./RotationSelector'),
    InformationPanel: () => import('./panels/InformationPanel'),
    DigitalZoom: () => import('./panels/DigitalZoom'),
    ColorManipulation: () => import('./panels/ColorManipulation'),
    LayersPanel: () => import('./panels/LayersPanel'),
  },
  props: {
    activeImageIndex: String,
    activePanel: String,
    activeOverlay: String,
    activeOverlays: {
      type: Object,
      default: () => ({})
    },
    configUi: {
      type: Object,
      required: true
    },
    isFullscreen: Boolean,
    isAuditLogPanelDisplayed: Boolean,
    canOpenImagePanel: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    isPanelDisplayed(panel) {
      return this.configUi[`project-explore-${panel}`];
    },
    isOverlayActive(overlay) {
      if (Object.prototype.hasOwnProperty.call(this.activeOverlays, overlay)) {
        return Boolean(this.activeOverlays[overlay]);
      }
      return this.activeOverlay === overlay;
    },
    toggleImageOverlay(overlay) {
      if (!this.canOpenImagePanel) {
        return;
      }
      this.$emit('toggle-overlay', overlay);
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/viewer-layers';

.viewer-toolbar {
  @include clinical-workspace-rail;
  display: flex;
  flex: 0 0 3rem;
  font-size: 0.9em;
  border-right-width: 1px;
  border-right-style: solid;
  z-index: $viewer-z-toolbar;
}

.map-tools-list {
  padding: 0.35rem 0.25rem;
  margin: 0;
  list-style: none;
}

.map-tools-list > li {
  position: relative;
}

.map-tools-list > li > a {
  @include clinical-icon-control(2.42rem, 6px);
  position: relative;
  min-height: 2.25rem;
  margin-bottom: 0.18rem;
  font-size: 1.12rem;
  text-decoration: none;
  cursor: pointer;

  &.active {
    background: linear-gradient(180deg, rgba($primary, 0.22), rgba($primary, 0.13));
    border-color: rgba($primary, 0.52);
    box-shadow: inset 0 0 0 1px rgba($primary, 0.22), 0 0 0 2px rgba($primary, 0.09);
    color: $primary;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
}

.viewer-toolbar .panel-options {
  position: absolute;
  top: 0;
  left: 3rem;
  width: 24em;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  background: rgba(13, 25, 42, 0.98);
  color: $dark-text-primary;
  padding: 0.85em;
  border: 1px solid rgba($primary, 0.18);
  border-left: 0;
  border-radius: 0 8px 8px 0;
  box-shadow: 18px 22px 48px rgba(0, 0, 0, 0.34);
  z-index: $viewer-z-toolbar-panel;

  :deep(h1) {
    font-size: 1.5rem;
    padding-top: 0.3rem !important;
    padding-bottom: 1rem !important;
    background: $dark-bg-primary;
    color: $dark-text-primary;
  }

  :deep(table) {
    width: 100%;
    background: $dark-bg-primary;
    color: $dark-text-primary;
  }
}

.is-divider {
  height: 1px;
  margin: 0.45rem 0.25rem;
  background: linear-gradient(90deg, transparent, rgba(190, 214, 238, 0.18), transparent);
  border: 0;
}

@media (max-width: 768px) {
  .viewer-toolbar .panel-options {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-height: 70%;
    border-radius: 12px 12px 0 0;
  }
}
</style>
